import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyCompany } from '@components/templates';
import { SITE_URLS } from '@constants';
import { useSEOMetatags } from '@hooks/useSEOMetatags';
import { useData } from '@hooks/useData';
import { useGetMyAccountStoreDetail } from '@services/useGetMyAccountStoreDetail';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useNotifications } from '@hooks/useNotifications';

export function MyCompanyPage() {
    const { uuid } = useParams();
    const {
        request: requestMyAccountStore,
        error: myAccountStoreError,
        statusCode: myAccountStoreStatusCode,
    } = useGetMyAccountStoreDetail();
    const { myAccountStoreDetail, isLoadingMyAccountStoreDetail } = useData();
    const { t, i18n } = useTranslation();
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();

    const translations = {
        title: t('my-company-component.metatags.title'),
        description: t('my-company-component.metatags.description'),
    };

    // fetch store data
    useEffect(() => {
        !isLoadingMyAccountStoreDetail && requestMyAccountStore({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        if (myAccountStoreError && myAccountStoreStatusCode) {
            showDefaultNotification({ type: 'error', message: myAccountStoreError });
            navigate(`/${SITE_URLS.myCompanies}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountStoreError]);

    useSEOMetatags({
        title: translations.title,
        description: translations.description,
        url: SITE_URLS.myCompaniesEdit.replace(':uuid', `${uuid}`),
        index: false,
    });

    return (
        <MyCompany
            uuid={myAccountStoreDetail?.data?.uuid || ''}
            name={myAccountStoreDetail?.data?.commercial_name || ''}
            logo={{
                src: myAccountStoreDetail?.data?.logo_img?.src,
                type: myAccountStoreDetail?.data?.logo_img?.type,
                width: myAccountStoreDetail?.data?.logo_img?.width,
                height: myAccountStoreDetail?.data?.logo_img?.height,
                isDefaultLogo: myAccountStoreDetail?.data?.logo_img?.is_default_logo,
            }}
            bannerImage={myAccountStoreDetail?.data?.banner_image}
            email={myAccountStoreDetail?.data?.email || ''}
            phoneNumber={myAccountStoreDetail?.data?.mobile_phone_number || ''}
            aboutText={myAccountStoreDetail?.data?.about_text || ''}
            images={
                myAccountStoreDetail?.data?.images?.map((image) => {
                    return { src: image.src || '', visible: image.visible };
                }) || []
            }
            address={[
                myAccountStoreDetail?.data?.address?.street ?? '',
                `${myAccountStoreDetail?.data?.address?.postal_code ?? ''} - ${myAccountStoreDetail?.data?.address?.city ?? ''} - ${myAccountStoreDetail?.data?.address?.country ?? ''}`,
            ]}
            locations={[
                {
                    uuid: myAccountStoreDetail?.data?.uuid ?? '',
                    latitude: myAccountStoreDetail?.data?.address?.latitude ?? 0,
                    longitude: myAccountStoreDetail?.data?.address?.longitude ?? 0,
                },
            ]}
            schedule={[
                {
                    header: myAccountStoreDetail?.data?.schedule?.schedule_header_1 ?? '',
                    text: myAccountStoreDetail?.data?.schedule?.schedule_text_1 ?? '',
                },
                {
                    header: myAccountStoreDetail?.data?.schedule?.schedule_header_2 ?? '',
                    text: myAccountStoreDetail?.data?.schedule?.schedule_text_2 ?? '',
                },
            ]}
            website={myAccountStoreDetail?.data?.website ?? ''}
            type={myAccountStoreDetail?.data?.type ?? ''}
            socialLinks={myAccountStoreDetail?.data?.social_networks || []}
            services={(myAccountStoreDetail?.data?.services || []).map((item) => {
                return {
                    uuid: item.uuid || '',
                    name: item.name || '',
                    description: item.description || '',
                    price: item.price || null,
                    time: item.time || null,
                    externalLink: item.external_link || null,
                    scheduleType: item.schedule_type || '',
                };
            })}
            paymentAndPickupAtStoreEnabled={myAccountStoreDetail?.data?.payment_and_pickup_at_store_enabled}
            hasProducts={myAccountStoreDetail?.data?.has_products}
            /* #TODO */
            productHighlights={[]}
        />
    );
}
